// src/utils/startRouteForUser.js
// Détermine la route de destination après connexion selon le profil.
// Les collaborateurs (is_collaborator dans user_metadata OU entrée active
// dans la table collaborators) sont redirigés vers /delegated.

import { supabase } from "../lib/supabaseClient";
import { resolveProfileType, accountHasContent } from "./onboardingProfile";

/**
 * Vérifie si l'utilisateur connecté est un collaborateur actif
 * (entrée dans la table collaborators avec status = "active").
 * Retourne l'objet collaborateur ou null.
 */
export async function getActiveCollaboratorEntry(userId) {
  if (!userId) return null;
  try {
    const { data, error } = await supabase
      .from("collaborators")
      .select("id, role, created_by, can_view, can_edit, can_add_artworks, can_delete_artworks, can_export, can_generate_reports, can_access_ai")
      .eq("user_id", userId)
      .eq("status", "active")
      .limit(1)
      .maybeSingle();

    if (error) {
      console.warn("[startRouteForUser] collaborators check error:", error.message);
      return null;
    }
    return data || null;
  } catch (e) {
    console.warn("[startRouteForUser] collaborators check exception:", e);
    return null;
  }
}

/**
 * Détermine si l'utilisateur doit encore passer l'onboarding personnalisé.
 * On n'onboarde que les métiers « clients » (spec Onboarding §3) et jamais :
 *   - les comptes staff (admin / team / superadmin),
 *   - les collaborateurs délégués,
 *   - les comptes démo,
 *   - les partenaires purs transport / valuation (pas de parcours métier dédié).
 * Un onboarding déjà complété (onboarding_completed_at non nul) n'est jamais rejoué.
 */
export function needsOnboarding(profile) {
  if (!profile) return false;
  if (profile.onboarding_completed_at) return false;
  if (profile.is_demo === true) return false;

  const role = (profile.role || "user").toLowerCase();

  // Staff / délégués : pas d'onboarding métier.
  if (["admin", "superadmin", "team", "collaborator", "collaborateur"].includes(role)) {
    return false;
  }

  // Partenaires (assurance / transport / valorisation) : PAS d'onboarding
  // collectionneur. Ils arrivent direct sur leur espace (accueil état-vide).
  if (role === "partner") {
    return false;
  }

  // Collectionneur, fondation (foundation_mode), art advisor / expert,
  // family office, notaire : tous onboardables.
  return true;
}

/**
 * Route synchrone basée uniquement sur le profil DB.
 * Utilisée en fallback si la vérification async n'est pas possible.
 */
function homeRouteForProfile(profile) {
  if (!profile) return "/dashboard";
  const role = (profile.role || "user").toLowerCase();

  // Collaborateur explicitement marqué
  if (role === "collaborator" || role === "collaborateur") return "/delegated";

  // Expert / Advisor
  if (role === "expert" || role === "art_advisor" || role === "advisor") {
    return "/expert/dashboard";
  }

  // Family Office
  if (role === "family_office") return "/family-office/dashboard";

  // Partenaire
  if (role === "partner") {
    const pt = (profile.partner_type || "").toLowerCase();
    if (pt === "transport") return "/partner/transport";
    if (pt === "insurance") return "/partner/insurance";
    if (pt === "valuation") return "/partner/valuation";
    if (pt === "notaire") return "/notaire";
    return "/partner";
  }

  // Notaire prescripteur
  if (role === "notaire") return "/notaire";

  // Membre de l'équipe → son espace de travail (coins délégués)
  if (role === "team") return "/team/dashboard";

  // Admin
  if (role === "admin" || role === "superadmin") return "/admin";

  // Directeur de fondation (collectionneur avec foundation_mode)
  if (profile.foundation_mode === true) return "/foundation/dashboard";

  // Collectionneur standard
  return "/dashboard";
}

export function startRouteForUser(profile) {
  if (!profile) return "/dashboard";

  // Gate onboarding — prioritaire sur toute redirection métier.
  if (needsOnboarding(profile)) return "/onboarding";

  return homeRouteForProfile(profile);
}

/**
 * Version async — vérifie aussi la table collaborators.
 * À utiliser dans Login.jsx et AcceptCollaboratorInvite.jsx.
 */
export async function startRouteForUserAsync(profile, session) {
  const meta = session?.user?.user_metadata || {};
  const sessionUserId = session?.user?.id || null;

  // Cas critique après signup collaborateur : auth.users existe, mais public.profiles
  // peut arriver quelques centaines de ms plus tard. On route quand même grâce
  // aux metadata ou à la table collaborators.
  if (meta.is_collaborator === true) return "/delegated";

  if (!profile) {
    const collabEntry = await getActiveCollaboratorEntry(sessionUserId);
    if (collabEntry) return "/delegated";
    return "/dashboard";
  }

  const role = (profile.role || "user").toLowerCase();

  // Rôle collaborateur explicite dans le profil
  if (role === "collaborator" || role === "collaborateur") return "/delegated";

  // Vérifier la table collaborators (cas où le profil n'a pas encore le bon rôle)
  const userId = profile.id || sessionUserId;
  if (userId && role === "user") {
    const collabEntry = await getActiveCollaboratorEntry(userId);
    if (collabEntry) return "/delegated";
  }

  // Gate onboarding « premiers pas » — mais on ne le force PAS si le compte a
  // déjà du contenu (invité, migré, admin, legacy) : on l'envoie à son espace.
  if (needsOnboarding(profile)) {
    const ptype = resolveProfileType(profile);
    // Un compte existant retourne sur la home de son rôle. On évite ici une
    // seconde table de correspondance susceptible d'envoyer le Family Office
    // vers la page administrative /family-office/manage.
    if (await accountHasContent(ptype)) return homeRouteForProfile(profile);
    return "/onboarding";
  }

  // Routing standard
  return startRouteForUser(profile);
}
